import { type FC, useEffect, useRef, useState } from 'react';

import { Container } from '../Box';
import { Text } from '../Typography';
import {
  linkStyles,
  listStyles,
  rootStyles,
  scrollAreaStyles,
  sentinelStyles,
} from './SectionNav.styles';
import { type SectionNavProps } from './SectionNav.types';

const SectionNav: FC<SectionNavProps> = ({
  items,
  ariaLabel = 'On this page',
  stickyOffset = 0,
  className,
}) => {
  const [activeId, setActiveId] = useState<string | undefined>(items[0]?.id);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isStuck, setIsStuck] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Publish the rendered height so content that sticks below the nav, such as
  // table titles, can offset itself without hard-coding the nav's size.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const root = document.documentElement;
    const update = () =>
      root.style.setProperty('--section-nav-height', `${nav.offsetHeight}px`);
    update();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(nav);
    return () => {
      observer?.disconnect();
      root.style.removeProperty('--section-nav-height');
    };
  }, []);

  // The sentinel stays at the nav's resting position, so the nav is stuck
  // once it sits below the sentinel.
  useEffect(() => {
    const nav = navRef.current;
    const sentinel = sentinelRef.current;
    if (!nav || !sentinel) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const next =
        nav.getBoundingClientRect().top - sentinel.getBoundingClientRect().top >
        0.5;
      setIsStuck((current) => (current === next ? current : next));
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const sections = items
      .map((item) => document.getElementById(item.id))
      .filter((section): section is HTMLElement => Boolean(section));
    if (!sections.length) return undefined;

    const visible = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.boundingClientRect.top);
          } else {
            visible.delete(entry.target.id);
          }
        });
        const next = [...visible.entries()].sort((left, right) => {
          const leftTop = Math.abs(left[1]);
          const rightTop = Math.abs(right[1]);
          return leftTop - rightTop;
        })[0]?.[0];
        if (next) setActiveId(next);
      },
      { rootMargin: '-15% 0px -65% 0px', threshold: [0, 0.01] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return undefined;

    const updateScrollAffordance = () => {
      const nextCanScrollLeft = list.scrollLeft > 1;
      const nextCanScrollRight =
        list.scrollLeft < list.scrollWidth - list.clientWidth - 1;

      setCanScrollLeft((current) =>
        current === nextCanScrollLeft ? current : nextCanScrollLeft
      );
      setCanScrollRight((current) =>
        current === nextCanScrollRight ? current : nextCanScrollRight
      );
    };

    updateScrollAffordance();
    list.addEventListener('scroll', updateScrollAffordance, { passive: true });

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateScrollAffordance);
    resizeObserver?.observe(list);
    window.addEventListener('resize', updateScrollAffordance);

    return () => {
      list.removeEventListener('scroll', updateScrollAffordance);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateScrollAffordance);
    };
  }, [items]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !activeId) return;

    const activeItem = Array.from(
      list.querySelectorAll<HTMLElement>('[data-section-nav-id]')
    ).find((item) => item.dataset.sectionNavId === activeId);
    if (!activeItem) return;

    const itemStart = activeItem.offsetLeft;
    const itemEnd = itemStart + activeItem.offsetWidth;
    const visibleStart = list.scrollLeft;
    const visibleEnd = visibleStart + list.clientWidth;
    if (itemStart >= visibleStart && itemEnd <= visibleEnd) return;

    list.scrollTo({
      behavior: 'smooth',
      left: Math.max(
        0,
        itemStart - (list.clientWidth - activeItem.offsetWidth) / 2
      ),
    });
  }, [activeId]);

  const top =
    typeof stickyOffset === 'number' ? `${stickyOffset}px` : stickyOffset;

  return (
    <>
      <div aria-hidden="true" css={sentinelStyles} ref={sentinelRef} />
      <nav
        aria-label={ariaLabel}
        css={rootStyles}
        className={className}
        data-stuck={isStuck}
        ref={navRef}
        style={{ top }}
      >
        <Container>
          <div
            css={scrollAreaStyles}
            data-can-scroll-left={canScrollLeft}
            data-can-scroll-right={canScrollRight}
          >
            <ul css={listStyles} ref={listRef}>
              {items.map((item) => (
                <li key={item.id} data-section-nav-id={item.id}>
                  <a
                    href={`#${item.id}`}
                    css={linkStyles}
                    aria-current={activeId === item.id ? 'location' : undefined}
                  >
                    <Text as="span" size="sm" fontWeight="semibold">
                      {item.label}
                    </Text>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </nav>
    </>
  );
};

SectionNav.displayName = 'SectionNav';

export default SectionNav;
